import { captureFirstTouch } from '@/lib/firstTouch';
import { pullSessionFromUrl } from '@/lib/sessionData';
import { isInviteRole } from '@/lib/roles';
import { redirect } from '@/lib/redirect';
import { useAccountsStore } from '@/stores/accounts';
import { useUserStore } from '@/stores/user';
import { useBillingStore } from '@/stores/billing';
import AccountView from '@/views/AccountView/AccountView.vue';
import CheckoutView from '@/views/CheckoutView/CheckoutView.vue';
import ExitSurveyView from '@/views/ExitSurveyView/ExitSurveyView.vue';
import BillingPlansView from '@/views/BillingPlansView/BillingPlansView.vue';
import BillingPortalView from '@/views/BillingPortalView/BillingPortalView.vue';
import PasswordView from '@/views/PasswordView/PasswordView.vue';
import ProfileView from '@/views/ProfileView/ProfileView.vue';
import LoginView from '@/views/LoginView/LoginView.vue';
import LostPasswordView from '@/views/LostPasswordView/LostPasswordView.vue';
import ReferralApplyView from '@/views/ReferralApplyView/ReferralApplyView.vue';
import ShareView from '@/views/ShareView/ShareView.vue';
import SignupView from '@/views/SignupView/SignupView.vue';
import NotFoundView from '@/views/NotFoundView/NotFoundView.vue';
import OauthAuthorizeView from '@/views/OauthAuthorizeView/OauthAuthorizeView.vue';
import OnboardingView from '@/views/OnboardingView/OnboardingView.vue';
import { createRouter, createWebHistory, type RouteLocationNormalized, type RouteLocationRaw } from 'vue-router';

/**
 * A lifetime holder has nothing to buy, and the plan picker is actively dangerous for
 * them: dabble-rest's plan change and checkout both resolve "the subscription" to the
 * customer's newest live one — after a lifetime purchase, the $0 entitlement carrier —
 * and swap the chosen paid price onto it, which ends the lifetime and bills them for a
 * plan they already own outright. The portal hides Change Plan for `isLifetime`, but
 * old `?changePlan=1` links, bookmarks and stale tabs reach these routes directly.
 *
 * The one exception is the lifetime upgrade dabble-rest offers an owner: they may see the
 * plans, which then sell only that upgrade, and check out that upgrade alone.
 *
 * Runs after `beforeEach` has required auth. The views load billing data lazily, so on
 * a cold load fetch it here first; the store already handles every failure of that
 * fetch (no Stripe customer, network, expired session), and an unknown state lets the
 * navigation through exactly as before.
 */
async function guardLifetimeHolder(to: RouteLocationNormalized): Promise<boolean | RouteLocationRaw> {
  const billing = useBillingStore();
  if (!billing.customer) {
    try {
      await billing.fetchBillingOverview();
    } catch {
      return true;
    }
  }
  if (!billing.isLifetime) return true;
  const allowed = to.name === 'billing-plans' ? billing.canUpgradeLifetime : billing.isLifetimeUpgradeCheckout;
  return allowed || { name: 'portal', replace: true };
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      // `/` is never rendered — `beforeEach` hands authenticated users off to
      // the writer app (falling back to `/billing/portal` when the hand-off
      // can't be built) and sends anonymous ones to `/auth/login`. vue-router
      // requires a component, so use an empty inline render fn.
      path: '/',
      component: { render: () => null },
    },
    {
      // `/auth` (and `/auth/`) is never rendered — `beforeEach` redirects
      // to `/auth/login` (returning user) or `/auth/signup` (first-timer)
      // based on the `hasLoggedIn` flag, or sends authed users back to
      // wherever they came from.
      path: '/auth',
      component: { render: () => null },
      meta: { public: true },
    },
    {
      path: '/auth/signup',
      name: 'signup',
      component: SignupView,
      meta: { public: true },
    },
    {
      path: '/auth/login',
      name: 'login',
      component: LoginView,
      meta: { public: true },
    },
    {
      // Authed users hitting `/auth?referralCode=...` are routed here so
      // the code can be applied to their existing subscription via a modal.
      // Anonymous users still flow through SignupView as before.
      path: '/auth/referral',
      name: 'referral-apply',
      component: ReferralApplyView,
      meta: { requiresAuth: true },
    },
    {
      path: '/account/lost-password',
      name: 'lost-password',
      component: LostPasswordView,
      meta: { public: true },
    },
    {
      path: '/account/onboarding',
      name: 'onboarding',
      component: OnboardingView,
    },
    {
      path: '/account',
      component: AccountView,
      meta: { requiresAuth: true },
      children: [
        {
          path: 'password',
          name: 'password',
          component: PasswordView,
        },
        {
          path: 'profile',
          name: 'profile',
          component: ProfileView,
        },
        {
          path: 'settings',
          redirect: { name: 'profile' },
        },
        {
          path: 'share',
          name: 'share',
          component: ShareView,
        },
        {
          path: 'referral',
          redirect: { name: 'share' },
        },
      ],
    },
    {
      path: '/billing',
      meta: { requiresAuth: true },
      component: AccountView,
      children: [
        {
          // Bare `/billing` is what marketing pages link to (the 60-Day Novel
          // Challenge winner's button, `/billing?coupon=CODE`); the portal is the
          // page they mean.
          path: '',
          redirect: { name: 'portal' },
        },
        {
          path: 'portal',
          name: 'portal',
          component: BillingPortalView,
        },
        {
          // Promo links used to bounce through a dedicated coupon step here; the
          // code is now applied in BillingCouponLinkModal over the page itself.
          path: 'coupon',
          redirect: { name: 'portal' },
        },
        {
          path: 'exit-survey',
          name: 'exit-survey',
          component: ExitSurveyView,
        },
      ],
    },
    {
      // Plan selection and checkout run full-screen (no account sidebar) as one
      // focused checkout flow. Kept at their historical /billing/* paths so old
      // links and bookmarks keep working.
      path: '/billing/plans',
      name: 'billing-plans',
      component: BillingPlansView,
      meta: { requiresAuth: true },
      beforeEnter: guardLifetimeHolder,
    },
    {
      path: '/billing/checkout',
      name: 'billing-checkout',
      component: CheckoutView,
      meta: { requiresAuth: true },
      beforeEnter: guardLifetimeHolder,
    },
    {
      // The `/billing/*` auth screens are the way *into* the billing portal.
      // They render the same forms as `/auth/*`, but pin the post-auth
      // destination to `/billing/portal` instead of letting it default to the
      // writer app — see the `billingAuth` branch in `beforeEach`.
      //
      // `/billing/auth` is never rendered; it dispatches to login or signup on
      // `hasLoggedIn`, exactly as `/auth` does.
      path: '/billing/auth',
      component: { render: () => null },
      meta: { public: true, billingAuth: true },
    },
    {
      path: '/billing/login',
      name: 'billing-login',
      component: LoginView,
      meta: { public: true, billingAuth: true },
    },
    {
      path: '/billing/signup',
      name: 'billing-signup',
      component: SignupView,
      meta: { public: true, billingAuth: true },
    },
    {
      // "Sign in with Dabble" for our other sites (the Circle community): they
      // send people here, and the view sends them back signed in. An anonymous
      // visitor takes the usual bounce through the login form — `beforeEach`
      // stashes this URL, query and all, as the post-auth destination.
      path: '/oauth/authorize',
      name: 'oauth-authorize',
      component: OauthAuthorizeView,
      meta: { requiresAuth: true },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: NotFoundView,
    },
  ],
});

/**
 * Read the `hasLoggedIn` marker from one storage area. Firefox throws
 * NS_ERROR_FAILURE from `getItem` when the profile's site storage is broken
 * (DABBLE-ACCOUNTS-2-4C), and Safari's private mode can throw too. The marker
 * only picks between the login and signup forms, so an unreadable store counts
 * as "no record" and the visitor still lands on a form.
 */
function readLoggedInMarker(storage: () => Storage): boolean {
  try {
    return storage().getItem('hasLoggedIn') === '1';
  } catch {
    return false;
  }
}

/** Has this browser completed a login before — this tab, or any earlier one? */
function hasLoggedInBefore() {
  return readLoggedInMarker(() => sessionStorage) || readLoggedInMarker(() => localStorage);
}

/**
 * Hand an already-authenticated user off to their redirect target (the writer
 * app by default). `redirect()` navigates away itself, so cancel the in-app
 * navigation on success; if it can't build the hand-off — an invalid stored
 * URL — keep them here in the portal.
 */
async function handOff() {
  try {
    await redirect();
    return false;
  } catch {
    return { path: '/billing/portal', replace: true };
  }
}

router.beforeEach(async (to, from) => {
  if (typeof window !== 'undefined') {
    // Before the session params are pulled: campaign tags stay in the URL either
    // way, but read them off the address the visitor actually arrived on.
    captureFirstTouch(new URL(window.location.href));
    pullSessionFromUrl(new URL(window.location.href));
  }

  // Wait for auth resolution before deciding redirects. init() is
  // idempotent and cached, so this resolves immediately on every
  // navigation after the first.
  await useAccountsStore().init();

  const { uid, email } = useUserStore().user;
  const isAuthed = Boolean(uid && email);

  // A `/billing/*` auth screen pins the post-auth destination to the portal,
  // overwriting whatever `redirect` this tab was carrying — that's the whole
  // reason these routes exist, and a stale value from an earlier bounce must
  // not hijack them. Use `/auth/login?redirect=…` to choose a destination.
  if (to.meta.billingAuth) {
    sessionStorage.setItem('redirect', `${location.origin}/billing/portal`);
  }

  // A promo link's code is in sessionStorage by now (pullSessionFromUrl above);
  // BillingCouponLinkModal applies it over the page. Drop it from the URL too, or
  // the router writes it back and a refresh would run the code a second time.
  // Anonymous visitors keep it in the post-login redirect below.
  if (isAuthed && 'coupon' in to.query) {
    const { coupon: _coupon, ...query } = to.query;
    return { path: to.path, query, hash: to.hash, replace: true };
  }

  if (to.path === '/') {
    if (!isAuthed) return { path: '/auth/login', replace: true };
    // Signed in at the root: hand off to the writer app, the same as any other
    // redirect with nothing specific stashed. `/billing/portal` remains the
    // fallback when the hand-off can't be built.
    return await handOff();
  }

  if (to.path === '/auth' || to.path === '/auth/') {
    if (isAuthed) {
      // pullSessionFromUrl above has already moved any ?referralCode= into
      // sessionStorage. If one is pending, divert to the apply-modal view
      // before the normal post-auth redirect.
      if (sessionStorage.getItem('referralCode')) {
        return { path: '/auth/referral', replace: true };
      }
      return await handOff();
    }
    return { path: hasLoggedInBefore() ? '/auth/login' : '/auth/signup', replace: true };
  }

  // `/billing/auth` mirrors `/auth`, but keeps the user in the billing portal.
  if (to.path === '/billing/auth' || to.path === '/billing/auth/') {
    if (isAuthed) {
      if (sessionStorage.getItem('referralCode')) {
        return { path: '/auth/referral', replace: true };
      }
      // The destination is this same app, so route straight there rather than
      // round-tripping through `redirect()` — no full reload.
      sessionStorage.removeItem('redirect');
      return { path: '/billing/portal', replace: true };
    }
    return { path: hasLoggedInBefore() ? '/billing/login' : '/billing/signup', replace: true };
  }

  // Invited collaborators (beta reader / editor) are almost always new users.
  // On their *initial* landing on the login form (the cross-origin bounce from
  // the app), forward first-time visitors to signup instead — mirrors the
  // `/auth` dispatcher's first-timer branch above. `from.matched.length === 0`
  // scopes this to that first navigation only, so an explicit in-app "Login"
  // click from the signup page is never trapped. Returning users (hasLoggedIn)
  // keep the login form. `role` is already in sessionStorage by now
  // (pullSessionFromUrl ran at the top).
  if (to.name === 'login' && !isAuthed && from.matched.length === 0) {
    if (!hasLoggedInBefore() && isInviteRole(sessionStorage.getItem('role'))) {
      return { path: '/auth/signup', replace: true };
    }
  }

  // Auth pages (login, signup) are always reachable, even when a user is
  // already signed in — Switch Account → "Add another account" depends on it.
  const isPublic = to.matched.some((record) => record.meta.public);
  if (isPublic || isAuthed) {
    return true;
  }

  sessionStorage.setItem('redirect', `${location.origin}${to.fullPath}`);
  // A ProfitWell Retain failed-payment email lands signed-out customers on
  // /billing/portal?__pw_iic=…. Its script loads async and reads that token off
  // the URL to offer a card update without signing in, so carry it onto the
  // login page instead of dropping it with the rest of the query.
  const pwToken = to.query.__pw_iic;
  if (typeof pwToken === 'string' && pwToken) {
    return { path: '/auth/login', query: { __pw_iic: pwToken }, replace: true };
  }
  return { path: '/auth/login', replace: true };
});

export default router;
